import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { Avatar, Box, Card, CardContent, CardMedia, Chip, CircularProgress, Divider, Fade, IconButton, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getBlogLikesAndComments, getBlogLikesAndCommentsOfUser } from '../../API/API';
import { titleToSlug } from '../../components/common/Util/Util';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: '1px solid #f1f5f9',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
    opacity: 0,
    transition: 'opacity 0.4s ease',
    zIndex: 1,
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    '&::before': {
      opacity: 1,
    },
    '& .card-media': {
      transform: 'scale(1.1)',
    },
    '& .card-content': {
      transform: 'translateY(-4px)',
    },
    '& .action-buttons': {
      opacity: 1,
      transform: 'translateY(0)',
    }
  },
  '& .card-content': {
    transition: 'transform 0.4s ease',
    position: 'relative',
    zIndex: 2,
  }
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: '280px',
  borderRadius: '20px 20px 0 0',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.4s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)',
    pointerEvents: 'none',
  }
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  height: '100%',
  '&:hover': {
    textDecoration: 'none'
  }
}));

const AuthorInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 0),
}));

const AuthorDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& .MuiAvatar-root': {
    width: 40,
    height: 40,
    marginRight: theme.spacing(2),
    border: '3px solid #ffffff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  }
}));

const AuthorName = styled(Typography)(({ theme }) => ({
  color: '#374151',
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: '20px',
  background: 'linear-gradient(135deg, #374151 0%, #6B7280 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const MetaGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  color: '#6B7280',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '18px'
}));

const ShareIconButton = styled(IconButton)(({ theme }) => ({
  color: '#6B7280',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  border: '1px solid #e2e8f0',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    transform: 'translateY(-2px) rotate(8deg)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '22px',
  fontWeight: 700,
  lineHeight: '1.3',
  color: '#111827',
  marginBottom: theme.spacing(2),
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 0),
  borderColor: '#e2e8f0',
  opacity: 0.6,
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '15px',
  lineHeight: '1.6',
  color: '#6B7280',
  marginBottom: theme.spacing(3),
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 400,
}));

const MetaStats = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 'auto',
}));

const StatsGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  '& .stat': {
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  }
}));

const KnowledgeCard = ({ post }) => {
  const {
    id,
    title = 'Untitled',
    content = 'No content available',
    coverImage,
    createDatetime,
    publishedDate,
    published = false,
    isActive = true,
    keywords = []
  } = post;

  const [isLiked, setIsLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const user = useSelector((state) => state.user.user);
  const coverImageAsString = `${coverImage || ''}`;
  const primaryCoverImageUrl = (coverImageAsString.startsWith('http') || coverImageAsString.startsWith('/'))
    ? coverImageAsString
    : (coverImage && Object.values(coverImage)[0]);

  useEffect(() => {
    const fetchLikesAndComments = async () => {
      try {
        const response = await getBlogLikesAndComments(id);
        if (response.result) {
          setCommentsCount(response.result.totalComments || 0);
          setLikesCount(response.result.totalLikes || 0);
        }

        if (user?.ID || user?.id) {
          const userResponse = await getBlogLikesAndCommentsOfUser(id, user?.ID || user?.id);
          if (userResponse.result) {
            setIsLiked(userResponse.result.totalLikes > 0);
          }
        }
      } catch (error) {
        toast.error('Error fetching interactions');
      }
    };

    if (id) {
      fetchLikesAndComments();
    }
  }, [id, user]);

  const handleShare = (e) => {
    e.preventDefault();
    const currentUrl = window.location.origin + `/knowledge/${titleToSlug(title) || 'untitled'}`;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        toast.success('Link copied to clipboard!', {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .catch(err => {
        console.error('Failed to copy URL:', err);
        toast.error('Failed to copy link', {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getContentPreview = () => {
    let textContent = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\[CONTENT_IMAGE_\d+\]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    return textContent.length > 120 ? textContent.substring(0, 120) + '...' : textContent;
  };

  if (!published || !isActive) {
    return null;
  }

  return (
    <Fade in={true} timeout={800}>
      <StyledLink to={`/knowledge/${titleToSlug(title) || 'untitled'}`} state={{ blogData: post, id: id }}>
        <StyledCard>
          <StyledCardMedia
            className="card-media"
            image={primaryCoverImageUrl}
            title={title}
          />

          <Box className="action-buttons" sx={{ position: 'absolute', top: 16, right: 16 }}>
            <Tooltip title="Copy link to clipboard">
              <ShareIconButton onClick={handleShare} size="small">
                <ShareOutlinedIcon fontSize="small" />
              </ShareIconButton>
            </Tooltip>
          </Box>

          <CardContent className="card-content" sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <AuthorInfo>
              <AuthorDetails>
                <Avatar alt="Admin" />
                <Box>
                  <AuthorName>Admin</AuthorName>
                  <MetaGroup>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      {formatDate(publishedDate || createDatetime)}
                    </Typography>
                  </MetaGroup>
                </Box>
              </AuthorDetails>
            </AuthorInfo>

            <Title>
              {title}
            </Title>

            <Description>
              {getContentPreview()}
            </Description>

            {keywords && keywords.length > 0 && (
              <Box sx={{ mb: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {keywords.slice(0, 3).map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      color: '#667eea',
                      fontWeight: 600,
                      fontSize: '11px',
                      height: '24px',
                      '& .MuiChip-label': {
                        padding: '0 8px',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(102, 126, 234, 0.2)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                  />
                ))}
                {keywords.length > 3 && (
                  <Chip
                    label={`+${keywords.length - 3}`}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(156, 163, 175, 0.1)',
                      color: '#6b7280',
                      fontWeight: 600,
                      fontSize: '11px',
                      height: '24px',
                      '& .MuiChip-label': {
                        padding: '0 8px',
                      }
                    }}
                  />
                )}
              </Box>
            )}

            <StyledDivider />

            {(user?.ID || user?.id) && (
              <MetaStats>
                <StatsGroup>
                  <Box className="stat">
                    <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                    <Typography>{commentsCount}</Typography>
                  </Box>
                  <Box className="stat">
                    {isLiking ? (
                      <CircularProgress size={16} color="error" />
                    ) : isLiked ? (
                      <FavoriteIcon sx={{ fontSize: 18, color: '#EF4444' }} />
                    ) : (
                      <FavoriteBorderIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                    )}
                    <Typography>{likesCount}</Typography>
                  </Box>
                </StatsGroup>
              </MetaStats>
            )}
          </CardContent>
        </StyledCard>
      </StyledLink>
    </Fade>
  );
};

export default KnowledgeCard;


